import { computed, defineComponent, ref } from 'vue'
import type { KnobTooltipProps } from './types'
import Bubbletip from 'components/bubbletip'
import BasicInput from 'components/form/basic-input'
import { useKeyboard } from '@/utils/hooks/useKeyboard'
import { clamp } from 'math'
import { reactiveComputed } from '@vueuse/core'
import { Grid } from 'components/grid'
import { defineVariants } from '@/utils/style/variant'

const useVariants = defineVariants({
  slots: {
    inputWrapper: {
      class: 'p-4'
    }
  },
  variants: {
    hasUserContent: {
      false: {
        inputWrapper: {
          class: 'px-4 pb-4 py-0'
        }
      }
    }
  }
})

const KnobTooltipInner = defineComponent<KnobTooltipProps>({
  setup(props) {
    const state = reactiveComputed<{
      value: number
      // stringValue: string
    }>(() => {
      const knobVal = props.knob.getValue();//props.knob.value
      const val = typeof knobVal === 'number' ? knobVal : 0
      return {
        value: val
        // stringValue: val + ''
      }
    })

    const handleInput = (ev: Event) => {
      const target = ev.target as HTMLInputElement
      //state.stringValue = target.value + ''
      state.value = Number(target.value)
    }

    const kb = useKeyboard()
    kb.addEventListener('key', (btn) => {
      const knob = props.knob
      const r = props.knob.range;
      const min = r.min
      const max = r.max

      if (!props.visible) return
      if (!props.visible.value) return

      if (btn.key === 'Enter' || btn.key === 'ArrowUp' || btn.key === 'ArrowDown') {
        let val = r.step ? Math.floor(state.value) : state.value //parseFloat(state.stringValue);

        if (r.step) {
          val = Math.ceil(val / r.step) * r.step
        }
        val = clamp(val, min, max)
        state.value = knob.setValue(val, r)
        //state.stringValue = state.value + '';
      }
    })

    const numberPrecision = computed(() => {
      const r = props.knob.range
      if (typeof r.step === 'number' && r.step > 0) return r.step + ''
      if (r.max <= 1.0) return `.01`
      return undefined
    })

    const userTooltip = computed(() => props.knob.range?.toolTip);

    const hasUserContent = computed(() => !(userTooltip.value?.body || userTooltip.value?.text));

    const rowTemplate = computed(() => {
      const count = Number(!!userTooltip.value?.body) + Number(!!userTooltip.value?.text);
      if (count <= 0) return 'auto';
      if (count <= 1) return 'max-content auto';
      if (count <= 2) return 'max-content max-content auto';
      return "auto";
    });


    const TopBar = (props: { title: string }) => {
      return <div class='bg-gray-200 h-12 w-full px-4 flex items-center overflow-hidden'>
        <div class="block text-sm font-light text-gray-500 dark:text-white">
           {props.title}
        </div>
      </div>
    }

    const variants = useVariants({ hasUserContent });

    return () => {
      return (
        <Grid
          style={{
            pointerEvents: 'all'
          }}
          noDefaultLayout
          rows={rowTemplate.value}
          gap="1rem"
          class="w-full h-full overflow-hidden rounded-lg"
        >
          {userTooltip.value?.text ? (
             TopBar({ title: userTooltip.value.text  })
          ) : null}
          {userTooltip.value?.body ? <div class='px-4'>
            <userTooltip.value.body />
          </div> : null}

          <div class={variants.value.inputWrapper.class}>
            <BasicInput
              numberPrecision={numberPrecision.value}
              keepZero
              type="number"
              onInput={handleInput}
              value={state.value}
            />
          </div>
        </Grid>
      )
    }
  }
})

KnobTooltipInner.props = ['knob', 'visible', 'knobRef']

const KnobTooltip = defineComponent<KnobTooltipProps>({
  setup(props) {
    const visibility = ref<number>(0)
    const isVisible = computed(() => visibility.value > 0.5);

    const userTooltip = computed(() => props.knob.range.toolTip);

    const sizeVariant = computed(() => {
      if (userTooltip.value?.body || userTooltip.value?.text) return undefined;
      return "s";
    })

    return () => {
      return (
        <Bubbletip
          variant="white"
          noPadding
          size={sizeVariant.value}
          hoverable={true}
          triggerRef={props.knobRef}
          body={() => <KnobTooltipInner knobRef={props.knobRef} knob={props.knob} visible={isVisible} />}
          visible={props.knob.dragging}
          active={props.knob.dragging}
          outputVisibility={visibility}
        />
      )
    }
  }
})

KnobTooltip.props = ['knob', 'knobRef']

export default KnobTooltip
